import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { analyticsAPI } from "../lib/api";
import { formatCurrency } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { setActivationVisitFlag } from "../components/ActivationChecklistWidget";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  ArrowRight,
  Lightbulb,
  BarChart2,
  Sparkles,
  Info,
  ShieldAlert,
  Target,
  Gauge,
} from "lucide-react";

const REASON_OPTIONS = [
  { value: "budget_too_low", label: "Budget too low" },
  { value: "lifestyle_change", label: "Lifestyle change" },
  { value: "temporary", label: "Temporary situation" },
  { value: "poor_control", label: "Poor spending control" },
  { value: "other", label: "Other" },
];

function NetBadge({ value, currency }) {
  const isPositive = value >= 0;
  return (
    <span
      className={`font-semibold tabular-nums ${
        isPositive ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
      }`}
    >
      {isPositive ? "+" : ""}
      {formatCurrency(value, currency)}
    </span>
  );
}

function TrendOverviewSection({ months, insights, currency }) {
  if (!months || months.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart2 className="h-5 w-5" />
            Trend Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No locked budget months found. Lock a budget plan to start seeing trends.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart2 className="h-5 w-5" />
          Trend Overview
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Monthly table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Month</th>
                <th className="text-right py-2 px-4 font-medium text-muted-foreground">Income</th>
                <th className="text-right py-2 px-4 font-medium text-muted-foreground">Expenses</th>
                <th className="text-right py-2 pl-4 font-medium text-muted-foreground">Net</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m) => (
                <tr key={`${m.year}-${m.month}`} className="border-b border-border last:border-0">
                  <td className="py-2.5 pr-4 font-medium">{m.label}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-green-600 dark:text-green-400">
                    {formatCurrency(m.income, m.currency || currency)}
                  </td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-red-600 dark:text-red-400">
                    {formatCurrency(m.expenses, m.currency || currency)}
                  </td>
                  <td className="py-2.5 pl-4 text-right">
                    <NetBadge value={m.net} currency={m.currency || currency} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Auto-generated insights */}
        {insights && insights.length > 0 && (
          <div className="space-y-2 pt-2">
            {insights.map((insight, i) => (
              <div
                key={`insight-${i}-${insight.slice(0, 20)}`}
                className="flex items-start gap-2 p-3 rounded-md bg-muted/50 text-sm"
              >
                <Lightbulb className="h-4 w-4 mt-0.5 flex-shrink-0 text-yellow-500" />
                <span>{insight}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PatternDetectionSection({ patterns }) {
  if (!patterns || patterns.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Pattern Detection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Not enough data to detect patterns. Patterns appear after 2+ locked months with
            consistent behavior.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Pattern Detection
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {patterns.map((p) => (
            <li key={p.category} className="flex items-start gap-3 p-3 rounded-md bg-muted/50">
              {p.type === "overspend" ? (
                <TrendingDown className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-500" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-500" />
              )}
              <span className="text-sm">{p.message}</span>
              <Badge
                variant="outline"
                className={`ml-auto text-xs flex-shrink-0 ${
                  p.type === "overspend"
                    ? "border-red-500/30 text-red-600 bg-red-500/10"
                    : "border-green-500/30 text-green-600 bg-green-500/10"
                }`}
              >
                {p.type === "overspend" ? "Overspend" : "Stable"}
              </Badge>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function GuidedReflectionSection({ patterns, savedReflections, onSaved }) {
  const overspendPatterns = patterns.filter((p) => p.type === "overspend");

  // Only show categories that have NOT already been reflected on
  const reflectedCategories = new Set(savedReflections.map((r) => r.category));
  const pending = overspendPatterns.filter((p) => !reflectedCategories.has(p.category));

  if (pending.length === 0) return null;

  return (
    <div className="space-y-4">
      {pending.map((pattern) => (
        <ReflectionForm key={pattern.category} pattern={pattern} onSaved={onSaved} />
      ))}
    </div>
  );
}

function ReflectionForm({ pattern, onSaved }) {
  const [selected, setSelected] = useState("");
  const [otherText, setOtherText] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!selected) {
      toast.error("Please select a reason.");
      return;
    }
    if (selected === "other" && !otherText.trim()) {
      toast.error("Please describe the reason.");
      return;
    }
    setSaving(true);
    try {
      const res = await analyticsAPI.saveReflection({
        category: pattern.category,
        reason: selected,
        reason_text: selected === "other" ? otherText.trim() : null,
      });
      onSaved(res.data);
      toast.success("Reflection saved.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save reflection.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-orange-500/30">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <HelpCircle className="h-5 w-5 text-orange-500" />
          Help us understand your spending pattern
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          What best explains your{" "}
          <span className="font-semibold text-foreground">{pattern.category}</span> spending?
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2">
          {REASON_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                selected === opt.value
                  ? "border-primary bg-primary/10"
                  : "border-border hover:bg-muted/50"
              }`}
            >
              <input
                type="radio"
                name={`reason-${pattern.category}`}
                value={opt.value}
                checked={selected === opt.value}
                onChange={() => setSelected(opt.value)}
                className="accent-primary"
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>

        {selected === "other" && (
          <textarea
            className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            rows={3}
            placeholder="Please describe..."
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
          />
        )}

        <Button onClick={handleSubmit} disabled={saving || !selected} size="sm">
          {saving ? "Saving…" : "Save reflection"}
        </Button>
      </CardContent>
    </Card>
  );
}

function TensionBuilderSection({ savedReflections }) {
  const navigate = useNavigate();

  if (savedReflections.length === 0) return null;

  return (
    <Card className="border-primary/40 bg-primary/5">
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0 text-orange-500" />
          <p className="text-sm font-medium leading-relaxed">
            Your current budget may no longer reflect your reality.
          </p>
        </div>
        <Button
          onClick={() => navigate("/budget")}
          className="flex items-center gap-2"
        >
          Adjust my budget
          <ArrowRight className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}

function SystemStateCard({ message }) {
  return (
    <Card>
      <CardContent className="pt-5 flex items-start gap-3">
        <Info className="h-5 w-5 mt-0.5 flex-shrink-0 text-blue-500" />
        <p className="text-sm leading-relaxed">{message}</p>
      </CardContent>
    </Card>
  );
}

function SuggestionsTab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editedValues, setEditedValues] = useState({});
  const [applying, setApplying] = useState(false);

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await analyticsAPI.getSuggestions();
      setData(res.data);
      // Initialise editable values from suggested_budget (kept as numbers)
      const initial = {};
      (res.data.categories || []).forEach((c) => {
        initial[c.category] = c.suggested_budget;
      });
      setEditedValues(initial);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load suggestions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data || data.locked_months_count === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            No locked budget months found. Lock at least one budget plan to see suggestions.
          </p>
        </CardContent>
      </Card>
    );
  }

  const currency = data.currency || "PHP";
  const categories = data.categories || [];

  // Recalculate totals based on current editable values
  const totalSuggested = categories.reduce((sum, c) => {
    const val = parseFloat(editedValues[c.category] || 0) || 0;
    return sum + val;
  }, 0);
  const totalCurrent = categories.reduce((sum, c) => sum + c.current_budget, 0);
  const latestIncome = data.impact?.latest_income || 0;
  const estimatedNet = latestIncome - totalSuggested;
  const differenceVsCurrent = totalSuggested - totalCurrent;

  const handleValueChange = (category, value) => {
    // Store as number when valid, otherwise keep raw string for user editing
    const parsed = parseFloat(value);
    setEditedValues((prev) => ({ ...prev, [category]: isNaN(parsed) ? value : parsed }));
  };

  const handleApply = async () => {
    // Validate all inputs are valid numbers
    for (const cat of categories) {
      const val = parseFloat(editedValues[cat.category]);
      if (isNaN(val) || val < 0) {
        toast.error(`Invalid value for "${cat.category}". Please enter a valid amount.`);
        return;
      }
    }

    setApplying(true);
    try {
      const lines = categories.map((c) => ({
        category: c.category,
        allocated_budget: parseFloat(editedValues[c.category]) || 0,
        actual_expenditure: 0,
        row_state: "planned",
      }));
      await analyticsAPI.applySuggestions({ lines });
      const nm = data.next_month;
      toast.success(
        `Draft budget for ${nm ? new Date(nm.year, nm.month - 1).toLocaleString("default", { month: "long", year: "numeric" }) : "next month"} created. You can edit it in the Budget Planner.`
      );
      navigate("/budget");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply suggestions.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Section 1 — System State */}
      <SystemStateCard message={data.system_state_message} />

      {/* Section 2 — Current vs Suggested Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-5 w-5 text-yellow-500" />
            Budget Suggestions
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            You can adjust the suggested values before applying.
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Category</th>
                  <th className="text-right py-2 px-3 font-medium text-muted-foreground">Current Budget</th>
                  <th className="text-right py-2 px-3 font-medium text-muted-foreground">Avg Actual</th>
                  <th className="text-right py-2 pl-3 font-medium text-muted-foreground">Suggested Budget</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c.category} className="border-b border-border last:border-0">
                    <td className="py-2.5 pr-4 font-medium">{c.category}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-muted-foreground">
                      {formatCurrency(c.current_budget, currency)}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-muted-foreground">
                      {formatCurrency(c.avg_actual, currency)}
                    </td>
                    <td className="py-2.5 pl-3 text-right">
                      <div className="inline-flex flex-col items-end gap-0.5">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={editedValues[c.category] ?? ""}
                          onChange={(e) => handleValueChange(c.category, e.target.value)}
                          className="w-28 text-right px-2 py-1 rounded-md border border-border bg-background text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        {c.adjustment_label && (
                          <span className="text-xs text-muted-foreground italic max-w-[160px] text-right leading-tight">
                            {c.adjustment_label}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Section 3 — Impact Simulation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart2 className="h-5 w-5" />
            Impact Simulation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-muted-foreground">Total Suggested Expenses</span>
              <span className="font-semibold tabular-nums">{formatCurrency(totalSuggested, currency)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-muted-foreground">Latest Month Income</span>
              <span className="font-semibold tabular-nums text-green-600 dark:text-green-400">
                {formatCurrency(latestIncome, currency)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-muted-foreground">Estimated Net</span>
              <span
                className={`font-semibold tabular-nums ${
                  estimatedNet >= 0
                    ? "text-green-600 dark:text-green-400"
                    : "text-red-600 dark:text-red-400"
                }`}
              >
                {estimatedNet >= 0 ? "+" : ""}
                {formatCurrency(estimatedNet, currency)}
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Difference vs Current Budget</span>
              <span
                className={`font-semibold tabular-nums ${
                  differenceVsCurrent > 0
                    ? "text-orange-600 dark:text-orange-400"
                    : differenceVsCurrent < 0
                    ? "text-green-600 dark:text-green-400"
                    : "text-muted-foreground"
                }`}
              >
                {differenceVsCurrent > 0 ? "+" : ""}
                {formatCurrency(differenceVsCurrent, currency)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 4 — Action */}
      <Card className="border-primary/40 bg-primary/5">
        <CardContent className="pt-5 space-y-3">
          <p className="text-sm text-muted-foreground">
            Apply these suggestions to create a draft budget for next month. You can continue
            editing in the Budget Planner before locking.
          </p>
          <Button onClick={handleApply} disabled={applying} className="flex items-center gap-2">
            {applying ? "Applying…" : "Apply to Next Month Budget"}
            {!applying && <ArrowRight className="h-4 w-4" />}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Savings Analysis Tab ────────────────────────────────────────────────────

const SAVINGS_STORAGE_KEY = "savings_prev_snapshot";

const RISK_LABELS = {
  CRITICAL: "CRITICAL",
  HIGH_RISK: "HIGH RISK",
  UNSTABLE: "UNSTABLE",
  STABLE: "STABLE",
};

const RISK_BADGE_CLASSES = {
  CRITICAL: "border-red-500/30 text-red-600 bg-red-500/10 dark:text-red-400",
  HIGH_RISK: "border-orange-500/30 text-orange-600 bg-orange-500/10 dark:text-orange-400",
  UNSTABLE: "border-yellow-500/30 text-yellow-600 bg-yellow-500/10 dark:text-yellow-400",
  STABLE: "border-green-500/30 text-green-600 bg-green-500/10 dark:text-green-400",
};

const RISK_TEXT_CLASSES = {
  CRITICAL: "text-red-600 dark:text-red-400",
  HIGH_RISK: "text-orange-600 dark:text-orange-400",
  UNSTABLE: "text-yellow-600 dark:text-yellow-400",
  STABLE: "text-green-600 dark:text-green-400",
};

const RISK_STATEMENTS = {
  CRITICAL: "This places you in a critical position.",
  HIGH_RISK: "This places you in a high-risk position.",
  UNSTABLE: "This indicates an unstable financial position.",
  STABLE: "This meets the minimum threshold for financial stability.",
};

const POSITION_STATEMENTS = {
  CRITICAL: "Your current position is significantly below the required level.",
  HIGH_RISK: "Your current position falls short of that standard.",
  UNSTABLE: "You are approaching stability, but have not yet reached it.",
  STABLE: "You have reached a stable financial position.",
};

function buildMessageLines(data) {
  return [
    "Based on your current spending,",
    `your savings can sustain you for approximately ${data.survival_months.toFixed(1)} months.`,
    RISK_STATEMENTS[data.risk_level],
    "A stable financial buffer requires at least 6 months of coverage.",
    POSITION_STATEMENTS[data.risk_level],
  ];
}

function buildChangeMessage(drop) {
  if (drop >= 2) {
    return "Your financial buffer has dropped significantly. You are now in a high-risk position. Immediate adjustments are required to restore stability.";
  }
  if (drop >= 0.5) {
    return "Your financial buffer has decreased. Recent changes are moving you further from a stable position.";
  }
  return null;
}

function buildImprovementMessage(increase) {
  if (increase >= 2) {
    return "Your financial buffer has increased significantly. You are moving closer to a stable financial position.";
  }
  if (increase >= 0.5) {
    return "Your financial position has improved. Recent changes are strengthening your stability.";
  }
  return null;
}

function SavingsAnalysisTab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visibleLines, setVisibleLines] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [animationDone, setAnimationDone] = useState(false);
  const [changeMessage, setChangeMessage] = useState(null);
  const animTimerRef = useRef(null);

  useEffect(() => {
    analyticsAPI.getSavings()
      .then((res) => {
        const savings = res.data;
        setData(savings);

        if (!savings.has_data) return;

        const prev = (() => {
          try { return JSON.parse(localStorage.getItem(SAVINGS_STORAGE_KEY)); } catch (e) { console.warn("savings snapshot parse error", e); return null; }
        })();

        let shouldAnimate = false;
        let chgMsg = null;

        if (prev) {
          const delta = savings.survival_months - prev.survival_months;
          const absDelta = Math.abs(delta);
          const riskChanged = savings.risk_level !== prev.risk_level;

          if (absDelta >= 0.5 || riskChanged) {
            shouldAnimate = true;
            if (delta < 0) {
              chgMsg = buildChangeMessage(-delta);
            } else {
              chgMsg = buildImprovementMessage(delta);
            }
          }
        }

        setChangeMessage(chgMsg);

        const lines = buildMessageLines(savings);
        if (shouldAnimate) {
          setAnimating(true);
          setVisibleLines(0);
          setAnimationDone(false);
          let i = 0;
          const step = () => {
            i += 1;
            setVisibleLines(i);
            if (i < lines.length) {
              animTimerRef.current = setTimeout(step, 800);
            } else {
              setAnimating(false);
              setAnimationDone(true);
            }
          };
          animTimerRef.current = setTimeout(step, 700);
        } else {
          setVisibleLines(lines.length);
          setAnimationDone(true);
        }
      })
      .catch(() => {
        toast.error("Failed to load savings analysis.");
      })
      .finally(() => setLoading(false));

    return () => { if (animTimerRef.current) clearTimeout(animTimerRef.current); };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data || !data.has_data) {
    return (
      <Card>
        <CardContent className="pt-6 space-y-2">
          <p className="text-sm text-muted-foreground">
            No locked budget months or accounts found.
          </p>
          <p className="text-sm text-muted-foreground">
            Add accounts in <strong>Accounts &amp; Balances</strong> and lock at least one budget plan to see your savings analysis. Existing account balances are treated as opening balance snapshots and are included in your survival/runway calculation.
          </p>
        </CardContent>
      </Card>
    );
  }

  const currency = data.currency || "PHP";
  const progressPct = Math.min((data.survival_months / 6) * 100, 100);
  const messageLines = buildMessageLines(data);

  return (
    <div className="space-y-5">
      {/* Change / Improvement Message */}
      {changeMessage && (
        <div className="flex items-start gap-3 rounded-lg border border-orange-400/40 bg-orange-500/10 px-4 py-3 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-orange-500" />
          <span>{changeMessage}</span>
        </div>
      )}

      {/* Hero */}
      <Card>
        <CardContent className="pt-6 space-y-2 text-center">
          <p className={`font-mono text-5xl font-bold tracking-tight ${RISK_TEXT_CLASSES[data.risk_level]}`}>
            {data.survival_months.toFixed(1)}
          </p>
          <p className="text-lg font-semibold text-foreground">months</p>
          <p className="text-sm text-muted-foreground">Based on your current spending.</p>
          <div className="pt-2">
            <Badge
              variant="outline"
              className={`text-sm font-semibold px-3 py-1 ${RISK_BADGE_CLASSES[data.risk_level]}`}
            >
              {RISK_LABELS[data.risk_level]}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Progress Bar */}
      <Card>
        <CardContent className="pt-5 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground font-medium">Progress to 6-month buffer</span>
            <span className="font-mono font-semibold">
              {data.survival_months.toFixed(1)} / 6 months
            </span>
          </div>
          <Progress value={progressPct} className="h-3" />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>0</span>
            <span>6 months</span>
          </div>
        </CardContent>
      </Card>

      {/* Animated Message */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldAlert className="h-5 w-5" />
            Financial Reality Check
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {messageLines.map((line, i) => (
            <p
              key={i}
              className={`text-sm leading-relaxed transition-all duration-500 ${
                i < visibleLines
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-2 pointer-events-none select-none"
              }`}
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              {line}
            </p>
          ))}
        </CardContent>
      </Card>

      {/* Financial Breakdown */}
      {animationDone && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Financial Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            <div className="flex justify-between py-3 border-b border-border text-sm">
              <span className="text-muted-foreground">Protected Savings (from Accounts &amp; Balances)</span>
              <span className="font-mono font-semibold">{formatCurrency(data.total_savings, currency)}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-border text-sm">
              <span className="text-muted-foreground">Required Savings (6 months)</span>
              <span className="font-mono font-semibold">{formatCurrency(data.required_savings, currency)}</span>
            </div>
            <div className="flex justify-between py-3 text-sm">
              <span className="text-muted-foreground">Gap</span>
              <span className={`font-mono font-semibold ${data.gap > 0 ? RISK_TEXT_CLASSES[data.risk_level] : "text-green-600 dark:text-green-400"}`}>
                {data.gap > 0 ? `-${formatCurrency(data.gap, currency)}` : formatCurrency(0, currency)}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Plan */}
      {animationDone && data.monthly_target > 0 && (
        <Card>
          <CardContent className="pt-5">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Saving{" "}
              <strong className="text-foreground font-mono">
                {formatCurrency(data.monthly_target, currency)}/month
              </strong>{" "}
              will get you there in 6 months.
            </p>
          </CardContent>
        </Card>
      )}

      {/* CTA */}
      {animationDone && (
        <div>
          <Button onClick={() => navigate("/budget")} className="flex items-center gap-2">
            Adjust My Plan
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Coverage status helpers ────────────────────────────────────────────────
const COVERAGE_STATUS_CONFIG = {
  "MONTH SECURED": {
    variant: "default",
    className: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    icon: CheckCircle2,
  },
  "PARTIAL COVERAGE": {
    variant: "secondary",
    className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    icon: AlertTriangle,
  },
  "SURVIVAL NOT SECURED": {
    variant: "destructive",
    className: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    icon: ShieldAlert,
  },
};

const CONFIDENCE_CONFIG = {
  HIGH: { className: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" },
  MEDIUM: { className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" },
  LOW: { className: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" },
};

function CoverageStatusBadge({ status }) {
  const cfg = COVERAGE_STATUS_CONFIG[status] || COVERAGE_STATUS_CONFIG["SURVIVAL NOT SECURED"];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${cfg.className}`}>
      <Icon className="h-3 w-3" />
      {status}
    </span>
  );
}

function ConfidenceBadge({ level }) {
  const cfg = CONFIDENCE_CONFIG[level] || CONFIDENCE_CONFIG["LOW"];
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cfg.className}`}>
      {level}
    </span>
  );
}

// Build YYYY-MM options for the month selector
const PAST_MONTHS = 3;   // months of historical context shown before today
const FUTURE_MONTHS = 8; // months of forward planning horizon shown after today

function buildMonthOptions() {
  const options = [];
  const now = new Date();
  for (let i = -PAST_MONTHS; i <= FUTURE_MONTHS; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    options.push({ value, label });
  }
  return options;
}

const MONTH_OPTIONS = buildMonthOptions();
const DEFAULT_MONTH = MONTH_OPTIONS[PAST_MONTHS].value; // current month

// Default scenario income presets (PHP-denominated; overridable per workspace via the
// simulate_amounts query parameter on the /analytics/projected-coverage endpoint).
const SCENARIO_PRESETS = [50000, 70000, 100000];

function ProjectedCoverageTab() {
  const [selectedMonth, setSelectedMonth] = useState(DEFAULT_MONTH);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeScenario, setActiveScenario] = useState(null);

  const fetchCoverage = useCallback(async (month) => {
    setLoading(true);
    try {
      const res = await analyticsAPI.getProjectedCoverage(
        month,
        SCENARIO_PRESETS.join(",")
      );
      setData(res.data);
      setActiveScenario(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load projected coverage.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCoverage(selectedMonth);
  }, [fetchCoverage, selectedMonth]);

  const currency = data?.currency || "PHP";

  // Scenario data: use API-provided scenarios keyed by income amount
  const scenarioMap = {};
  (data?.scenarios || []).forEach((s) => {
    scenarioMap[s.income] = s.surplus_deficit;
  });

  return (
    <div className="space-y-5">
      {/* Month selector */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-muted-foreground whitespace-nowrap">
          Viewing month:
        </label>
        <select
          className="border border-border rounded-md px-3 py-1.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
        >
          {MONTH_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fetchCoverage(selectedMonth)}
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : !data ? null : (
        <>
          {/* ── Widget 1: Projected Coverage Summary ── */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Gauge className="h-5 w-5" />
                Projected Coverage — {data.month_label}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-0 divide-y divide-border">
              <div className="flex items-center justify-between py-3 text-sm">
                <span className="text-muted-foreground">Coverage Status</span>
                <CoverageStatusBadge status={data.coverage_status} />
              </div>
              <div className="flex items-center justify-between py-3 text-sm">
                <span className="text-muted-foreground">Confidence Level</span>
                <ConfidenceBadge level={data.confidence_level} />
              </div>
              <div className="flex items-center justify-between py-3 text-sm">
                <span className="text-muted-foreground">Minimum Survival Required</span>
                <span className="font-mono font-semibold">
                  {formatCurrency(data.survival_expense_total, currency)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 text-sm">
                <span className="text-muted-foreground">Total Required (all layers)</span>
                <span className="font-mono font-semibold">
                  {formatCurrency(data.total_required_expenses, currency)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 text-sm">
                <span className="text-muted-foreground">Confirmed Income</span>
                <span className="font-mono font-semibold text-green-600 dark:text-green-400">
                  {formatCurrency(data.confirmed_income_total, currency)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 text-sm">
                <span className="text-muted-foreground">Expected Income</span>
                <span className="font-mono font-semibold text-blue-600 dark:text-blue-400">
                  {formatCurrency(data.expected_income_total, currency)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 text-sm">
                <span className="text-muted-foreground">
                  {data.remaining_income_needed >= 0 ? "Remaining Needed" : "Surplus"}
                </span>
                <span
                  className={`font-mono font-semibold ${
                    data.remaining_income_needed > 0
                      ? "text-red-600 dark:text-red-400"
                      : "text-green-600 dark:text-green-400"
                  }`}
                >
                  {data.remaining_income_needed > 0
                    ? formatCurrency(data.remaining_income_needed, currency)
                    : `+${formatCurrency(Math.abs(data.remaining_income_needed), currency)}`}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* ── Widget 2: Coverage Meter ── */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart2 className="h-5 w-5" />
                Coverage Meter
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {[
                {
                  label: "Survival",
                  pct: data.survival_coverage_pct,
                  amount: data.survival_expense_total,
                  color: "bg-red-500",
                },
                {
                  label: "Survival + Stability",
                  pct: data.stability_coverage_pct,
                  amount: data.survival_expense_total + data.stability_expense_total,
                  color: "bg-yellow-500",
                },
                {
                  label: "Full Protection",
                  pct: data.protection_coverage_pct,
                  amount: data.total_required_expenses,
                  color: "bg-green-500",
                },
              ].map((row) => (
                <div key={row.label} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{row.label}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {row.pct.toFixed(1)}% of {formatCurrency(row.amount, currency)}
                    </span>
                  </div>
                  <Progress value={row.pct} className="h-2" />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* ── Widget 3: Gap Indicator ── */}
          <Card>
            <CardContent className="pt-5">
              {data.income_gap_to_survival > 0 ? (
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-700 dark:text-red-400">
                      You still need{" "}
                      <span className="font-mono">
                        {formatCurrency(data.income_gap_to_survival, currency)}
                      </span>{" "}
                      to secure survival this month.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Minimum survival income needed:{" "}
                      <span className="font-mono font-medium">
                        {formatCurrency(data.minimum_survival_income, currency)}
                      </span>
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-green-700 dark:text-green-400">
                      Survival is covered.{" "}
                      {data.remaining_income_needed < 0 && (
                        <>
                          You have a{" "}
                          <span className="font-mono">
                            {formatCurrency(Math.abs(data.remaining_income_needed), currency)}
                          </span>{" "}
                          buffer this month.
                        </>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Minimum survival income required:{" "}
                      <span className="font-mono font-medium">
                        {formatCurrency(data.minimum_survival_income, currency)}
                      </span>
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Widget 4: Scenario Simulator ── */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Target className="h-5 w-5" />
                Scenario Simulator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Tap a scenario to see your projected outcome if your income for this month equals that amount.
              </p>
              <div className="flex flex-wrap gap-2">
                {(data.scenarios || []).map((s) => (
                  <button
                    key={s.income}
                    onClick={() => setActiveScenario(activeScenario === s.income ? null : s.income)}
                    className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${
                      activeScenario === s.income
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-muted hover:bg-muted/80 border-border"
                    }`}
                  >
                    {formatCurrency(s.income, currency)}
                  </button>
                ))}
              </div>
              {activeScenario !== null && scenarioMap[activeScenario] !== undefined && (
                <div
                  className={`rounded-lg border p-4 text-sm ${
                    scenarioMap[activeScenario] >= 0
                      ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950"
                      : "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950"
                  }`}
                >
                  {scenarioMap[activeScenario] >= 0 ? (
                    <p className="text-green-800 dark:text-green-200">
                      With{" "}
                      <span className="font-mono font-semibold">
                        {formatCurrency(activeScenario, currency)}
                      </span>{" "}
                      income you would have a{" "}
                      <span className="font-mono font-semibold">
                        +{formatCurrency(scenarioMap[activeScenario], currency)}
                      </span>{" "}
                      surplus after all obligations.
                    </p>
                  ) : (
                    <p className="text-red-800 dark:text-red-200">
                      With{" "}
                      <span className="font-mono font-semibold">
                        {formatCurrency(activeScenario, currency)}
                      </span>{" "}
                      income you would still have a{" "}
                      <span className="font-mono font-semibold">
                        {formatCurrency(Math.abs(scenarioMap[activeScenario]), currency)}
                      </span>{" "}
                      deficit.
                    </p>
                  )}
                </div>
              )}
              {data.savings_targets_total > 0 && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Info className="h-3.5 w-3.5" />
                  Includes{" "}
                  <span className="font-mono font-medium">
                    {formatCurrency(data.savings_targets_total, currency)}
                  </span>{" "}
                  in required savings targets.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export default function AnalyticsPage() {
  const location = useLocation();
  const defaultTab = new URLSearchParams(location.search).get("tab") || "overview";

  // Activation checklist: signal "Visit dashboard analytics" as completed when this page mounts.
  const { user } = useAuth();
  useEffect(() => {
    // Only persist the flag once we have a real user ID.
    if (user?.id) {
      setActivationVisitFlag(user.id, "analytics_visited");
    }
  }, [user?.id]);

  const [summary, setSummary] = useState(null);
  const [savedReflections, setSavedReflections] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, reflectionsRes] = await Promise.all([
        analyticsAPI.getSummary(),
        analyticsAPI.getReflections(),
      ]);
      setSummary(summaryRes.data);
      setSavedReflections(reflectionsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load analytics.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleReflectionSaved = (newReflection) => {
    setSavedReflections((prev) => [newReflection, ...prev]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const currency = summary?.months?.[0]?.currency || "PHP";
  const hasPatterns = summary?.patterns?.some((p) => p.type === "overspend");

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Understand what is happening to your finances and why.
        </p>
      </div>

      <Tabs defaultValue={defaultTab} className="space-y-5">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="suggestions">Suggestions</TabsTrigger>
          <TabsTrigger value="savings">Accounts &amp; Savings</TabsTrigger>
          <TabsTrigger value="projected-coverage">Projected Coverage</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Section 1: Trend Overview */}
          <TrendOverviewSection
            months={summary?.months || []}
            insights={summary?.insights || []}
            currency={currency}
          />

          {/* Section 2: Pattern Detection */}
          <PatternDetectionSection patterns={summary?.patterns || []} />

          {/* Section 3: Guided Reflection (only if overspend patterns detected) */}
          {hasPatterns && (
            <div>
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-orange-500" />
                Guided Reflection
              </h2>
              <GuidedReflectionSection
                patterns={summary.patterns}
                savedReflections={savedReflections}
                onSaved={handleReflectionSaved}
              />
            </div>
          )}

          {/* Section 4: Tension Builder (only after at least one reflection) */}
          <TensionBuilderSection savedReflections={savedReflections} />
        </TabsContent>

        <TabsContent value="suggestions">
          <SuggestionsTab />
        </TabsContent>

        <TabsContent value="savings">
          <SavingsAnalysisTab />
        </TabsContent>

        <TabsContent value="projected-coverage">
          <ProjectedCoverageTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
